import Connections from '../../connections';
import {
  action, observable,
} from 'mobx';
import {
  createWifiPackage, getWifiPackageById, getWifiPackages, updateWifiPackage,
} from '../../axios/wifiPackagesRequests';
import {
  DefaultLanguage,
} from '../../constants/languages';
import {
  getHeader,
} from '../../helperFunctions/getHeader';
import { uploadSingleImage } from '../../helperFunctions/uploadService';
import { isImageArray } from '../storeInterfaces/featuredArticlesStore';
import { convertToRaw } from 'draft-js';
import { IWifiPackage, ISelectedWifiPackage, WifiPackageSubscription, UserWifiPackageSubscription } from 'mobx-store/storeInterfaces/wifiPackageInterface';
import { getUsersWifiPackagesSubscription, getUserWifiPackageDetails } from 'axios/requests';
import { RequestStatus } from 'components/GeneralFormView/types';

export class WifiPackagesStore {
  @observable wifiPackagesList: any[] = [];

  @observable wifiPackagesCount: number = 0;

  @observable state: RequestStatus = RequestStatus.IDLE;

  @observable userWifiPackageDetails: WifiPackageSubscription[] = [];

  @observable usersWifiPackagesSubscriptions: UserWifiPackageSubscription[] =
    [];

  @observable usersWifiPackagesSubscriptionsCount = 0;

  @observable selectedWifiPackage: ISelectedWifiPackage = {
    name: "",
    isActive: false,
    availableCountries: ["EG"],
    wifiPackageOption: [],
  };

  @observable loading = false;

  @action
  public async getWifiPackages(
    langCode: string = DefaultLanguage
  ): Promise<void> {
    try {
      this.loading = true;
      this.state = RequestStatus.LOADING;
      const config = {
        ...getHeader(langCode),
      };

      const response = await Connections.wadiDeglaAxios.httpGet(
        getWifiPackages,
        config
      );
      const { wifiPackages, count } = response.data;
      this.wifiPackagesList = this.restructureWifiPackagesList(wifiPackages);
      this.wifiPackagesCount = count;
      this.state = RequestStatus.SUCCESS;
    } catch (error) {
      this.state = RequestStatus.FAILED;
      this.loading = false;
      return Promise.reject(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async getWifiPackageById(
    id: number,
    langCode: string = DefaultLanguage
  ): Promise<void> {
    try {
      this.loading = true;
      const response = await Connections.wadiDeglaAxios.httpGet(
        getWifiPackageById(id),
        getHeader(langCode)
      );
      this.selectedWifiPackage = this.restructureWifiPackage(
        response.data.wifiPackage
      );
    } catch (error) {
      this.loading = false;
      return Promise.reject(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async createWifiPackage(
    newWifiPackage: IWifiPackage,
    langCode: string = DefaultLanguage
  ): Promise<void> {
    return new Promise<void>(async (res, rej) => {
      try {
        this.loading = true;
        const response = await Connections.wadiDeglaAxios.httpPost(
          createWifiPackage,
          newWifiPackage,
          getHeader(langCode)
        );
        res(response.data.wifiPackageId);
      } catch (error) {
        this.loading = false;
        rej(error);
      }
    });
  }

  @action.bound
  public async getUserWifiPackageDetails(
    userId: number,
    langCode: string = DefaultLanguage
  ): Promise<void> {
    try {
      this.state = RequestStatus.LOADING;
      const response = await Connections.wadiDeglaAxios.httpGet(
        getUserWifiPackageDetails(userId),
        getHeader(langCode)
      );
      this.userWifiPackageDetails = response.data;
      this.state = RequestStatus.SUCCESS;
    } catch (error) {
      this.state = RequestStatus.FAILED;
      return Promise.reject(error);
    }
  }

  @action.bound
  public async getUsersWifiPackagesSubscription(
    pageNumber: number,
    pageSize: number,
    filterValue = "",
    langCode: string = DefaultLanguage
  ): Promise<void> {
    try {
      this.state = RequestStatus.LOADING;
      const response = await Connections.wadiDeglaAxios.httpGet(
        getUsersWifiPackagesSubscription(pageNumber, pageSize, filterValue),
        getHeader(langCode)
      );
      const [usersWifiPackagesSubscriptions, count] = response.data;
      this.usersWifiPackagesSubscriptions = usersWifiPackagesSubscriptions;
      this.usersWifiPackagesSubscriptionsCount = count;
      this.state = RequestStatus.SUCCESS;
    } catch (error) {
      this.state = RequestStatus.FAILED;
      return Promise.reject(error);
    }
  }

  @action.bound
  public async updateWifiPackage(
    updatedWifiPackage: any,
    wifiPackageId: number,
    langCode: string = DefaultLanguage
  ): Promise<void> {
    try {
      if (isImageArray(updatedWifiPackage.imageURL)) {
        updatedWifiPackage.imageURL = await uploadSingleImage(
          updatedWifiPackage.imageURL
        );
      }
      if (updatedWifiPackage.steps) {
        updatedWifiPackage.steps = JSON.stringify(
          convertToRaw(updatedWifiPackage.steps.getCurrentContent())
        );
      }
      if (updatedWifiPackage.expirationDate) {
        updatedWifiPackage.expirationDate = new Date(
          updatedWifiPackage.expirationDate
        ).getTime();
      }
      await Connections.wadiDeglaAxios.httpPut(
        updateWifiPackage(wifiPackageId),
        updatedWifiPackage,
        getHeader(langCode)
      );
    } catch (error) {
      return Promise.reject(error);
    }
  }

  private restructureWifiPackagesList(wifiPackages: any[]): any[] {
    const WifiPackagesList = [];
    for (const wifiPackage of wifiPackages) {
      if (wifiPackage) {
        WifiPackagesList.push(this.restructureWifiPackage(wifiPackage));
      }
    }
    return WifiPackagesList;
  }

  private restructureWifiPackage(wifiPackage: any): any {
    const restructuredWifiPackage = {
      packageId: wifiPackage.packageId,
      name: wifiPackage.name,
      description: wifiPackage.description,
      wifiPackageOption: wifiPackage.wifiPackageOption,
      isActive: wifiPackage.isActive,
    };
    return restructuredWifiPackage;
  }
}
